import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import { PrizeFormData, Prize } from '../../types';
import { prizeService } from '../../services/prizeService';

interface CreatePrizeDialogProps {
  open: boolean;
  onClose: () => void;
  onPrizeCreated: () => void;
  teamId: string;
  coachId: string;
  /** When set, dialog is in edit mode: form prefilled and submit updates this prize */
  existingPrize?: Prize | null;
}

const CreatePrizeDialog: React.FC<CreatePrizeDialogProps> = ({
  open,
  onClose,
  onPrizeCreated,
  teamId,
  coachId,
  existingPrize = null
}) => {
  const isEdit = !!existingPrize?.id;
  const [formData, setFormData] = useState<PrizeFormData>({
    name: '',
    description: '',
    pointCost: 0,
    type: 'individual',
    category: 'physical',
    imageUrl: '',
    maxQuantity: undefined
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && existingPrize?.id) {
      setFormData({
        name: existingPrize.name,
        description: existingPrize.description,
        pointCost: existingPrize.pointCost ?? 0,
        type: existingPrize.type ?? 'individual',
        category: existingPrize.category ?? 'physical',
        imageUrl: existingPrize.imageUrl ?? '',
        maxQuantity: existingPrize.maxQuantity
      });
    }
  }, [open, existingPrize]);

  const handleInputChange = (field: keyof PrizeFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'pointCost' || field === 'maxQuantity' ? 
        (value === '' ? undefined : Number(value)) : 
        field === 'imageUrl' ? (value.trim() === '' ? undefined : value.trim()) : value
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!formData.name.trim() || !formData.description.trim() || formData.pointCost <= 0) {
      setError('Please fill in all required fields with valid values');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (isEdit && existingPrize?.id) {
        await prizeService.updatePrize(existingPrize.id, formData);
      } else {
        await prizeService.createPrize(formData, teamId, coachId);
      }
      onPrizeCreated();
      handleClose();
    } catch (err: any) {
      console.error('Error creating prize:', err);
      setError(err.message || 'Failed to create prize');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!isEdit) {
      setFormData({
        name: '',
        description: '',
        pointCost: 0,
        type: 'individual',
        category: 'physical',
        imageUrl: '',
        maxQuantity: undefined
      });
    }
    setError('');
    onClose();
  };

  const categoryOptions = [
    { value: 'physical', label: 'Physical Prize', icon: '🏆' },
    { value: 'digital', label: 'Digital Prize', icon: '💻' },
    { value: 'experience', label: 'Experience', icon: '🎯' },
    { value: 'custom', label: 'Custom', icon: '⭐' }
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit Prize' : 'Create New Prize'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Prize Name"
                value={formData.name}
                onChange={handleInputChange('name')}
                required
                disabled={loading}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Point Cost"
                type="number"
                value={formData.pointCost || ''}
                onChange={handleInputChange('pointCost')}
                required
                disabled={loading}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={handleInputChange('description')}
                required
                disabled={loading}
                placeholder="Describe what this prize is and why athletes would want it..."
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required disabled={loading}>
                <InputLabel>Prize Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={handleInputChange('type')}
                  label="Prize Type"
                >
                  <MenuItem value="individual">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>👤</span>
                      Individual Prize
                    </Box>
                  </MenuItem>
                  <MenuItem value="team">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>👥</span>
                      Team Prize
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required disabled={loading}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={handleInputChange('category')}
                  label="Category"
                >
                  {categoryOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{option.icon}</span>
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Image URL (optional)"
                value={formData.imageUrl}
                onChange={handleInputChange('imageUrl')}
                disabled={loading}
                placeholder="https://example.com/image.jpg"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Max Quantity (optional)"
                type="number"
                value={formData.maxQuantity || ''}
                onChange={handleInputChange('maxQuantity')}
                disabled={loading}
                inputProps={{ min: 1 }}
                placeholder="Leave empty for unlimited"
                helperText="Set a limit on how many times this prize can be redeemed"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Prize Type Help:</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              • <strong>Individual Prizes:</strong> Athletes redeem these with their personal points
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • <strong>Team Prizes:</strong> Anyone on the team can redeem these using the team's total points
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            {loading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Prize')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreatePrizeDialog;
